import { useState, useEffect } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import dayjs from 'dayjs';

import { partOrders, rmas } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ────────────────────────────────────────────────────────────────

const RETURN_REASONS = ['Defective', 'Wrong Part', 'Excess Stock', 'Warranty Claim', 'Other'];

const EMPTY_FORM = {
  partOrderId:  '',
  returnReason: '',
  returnedQty:  1,
  notes:        '',
};

// Only show fulfilled/shipped/completed orders that don't already have a pending/active RMA
const eligibleOrders = partOrders.filter(
  (o) => ['Fulfilled', 'Shipped', 'Completed'].includes(o.status)
);

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSave: (rma: object) => void,
 * }} props
 */
export default function CreateRMAModal({ open, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
  }, [open]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Resolve the selected order's details for display and submission
  const selectedOrder = eligibleOrders.find((o) => o.id === form.partOrderId) ?? null;

  const isValid =
    Boolean(form.partOrderId) &&
    Boolean(form.returnReason) &&
    Number(form.returnedQty) >= 1;

  const handleSave = () => {
    if (!selectedOrder || !isValid) return;

    // Generate next RMA number based on total existing count
    const nextNum = 1001 + rmas.length;
    const newRma = {
      id:              `f5a6b7c8-new-${Date.now()}`,
      rmaNumber:       `RMA-${nextNum}`,
      partOrderId:     selectedOrder.id,
      ticketNumber:    selectedOrder.ticketNumber,
      partNumber:      selectedOrder.partNumber,
      partDescription: selectedOrder.partDescription,
      customerName:    selectedOrder.customerName,
      returnReason:    form.returnReason,
      status:          'Pending',
      disposition:     null,
      inspectionNotes: form.notes.trim() || null,
      returnedQty:     Number(form.returnedQty),
      createdDate:     dayjs().format('YYYY-MM-DD'),
      receivedDate:    null,
      resolvedDate:    null,
      assignedTo:      null,
    };

    onSave(newRma);
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="sm"
      fullWidth
    >
      {/* ── Title row ────────────────────────────────────────────────────── */}
      <DialogTitle sx={{ fontWeight: 600,
        fontSize: '1rem',
        color: 'var(--grey-900)',
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        Create RMA
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}
        >
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>

        {/* Part Order selector */}
        <FormControl size="small" fullWidth sx={fieldSx}>
          <InputLabel>Part Order</InputLabel>
          <Select value={form.partOrderId} onChange={set('partOrderId')} label="Part Order">
            {eligibleOrders.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.ticketNumber} — {o.partNumber} ({o.customerName})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Auto-populated order details */}
        {selectedOrder && (
          <Box sx={{ backgroundColor: 'var(--grey-50)',
            border: '1px solid var(--grey-200)',
            borderRadius: '6px',
            px: 2,
            py: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}>
            {[
              { label: 'Ticket #',    value: selectedOrder.ticketNumber    },
              { label: 'Part #',      value: selectedOrder.partNumber      },
              { label: 'Description', value: selectedOrder.partDescription },
              { label: 'Customer',    value: selectedOrder.customerName    },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 80 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-800)', fontWeight: 500 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Return Reason + Qty row */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel>Return Reason</InputLabel>
            <Select value={form.returnReason} onChange={set('returnReason')} label="Return Reason">
              {RETURN_REASONS.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Qty Returned"
            type="number"
            inputProps={{ min: 1 }}
            value={form.returnedQty}
            onChange={set('returnedQty')}
            sx={{ minWidth: 120, ...fieldSx }}
          />
        </Box>

        {/* Notes */}
        <TextField
          size="small"
          label="Notes (optional)"
          multiline
          rows={3}
          value={form.notes}
          onChange={set('notes')}
          fullWidth
          sx={fieldSx}
        />

        {/* Initial status hint */}
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
          Initial status will be set to <strong>Pending</strong>.
        </Typography>

      </DialogContent>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem',
            color: 'var(--grey-500)',
            cursor: 'pointer',
            '&:hover': { color: 'var(--grey-700)' },
          }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isValid}
          onClick={handleSave}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2,
          }}
        >
          Create RMA
        </Button>
      </DialogActions>
    </Dialog>
  );
}
